import {
  FiAward,
  FiBriefcase,
  FiChevronRight,
  FiDownload,
  FiFileText,
  FiMapPin,
} from "react-icons/fi";
import GitHubCalendar from "react-github-calendar";
import { Link } from "react-router-dom";

import { SOCIAL_ICONS } from "../components/layout/nav";
import Card from "../components/ui/Card";
import DifficultyBadge from "../components/ui/DifficultyBadge";
import LazyImage from "../components/ui/LazyImage";
import LeetCodeActivity from "../components/ui/LeetCodeActivity";
import ProgressRing from "../components/ui/ProgressRing";
import TopicPill from "../components/ui/TopicPill";
import certificates from "../data/certificates";
import { experiences } from "../data/experience";
import { profile } from "../data/profile";
import { difficultyCounts, numberedProjects } from "../data/projects";
import { languages, skillTiers, totalTopics } from "../data/skills";
import { getBrandColor } from "../lib/brandColors";
import getTechIcon from "../lib/techIcons";
import { useTheme } from "../theme/ThemeContext";

// This calendar version expects an explicit { level0..level4 } theme object
// (no colorScheme prop), so we pick the palette by mode.
const CALENDAR_THEMES = {
  light: {
    level0: "#ebedf0",
    level1: "#9be9a8",
    level2: "#40c463",
    level3: "#30a14e",
    level4: "#216e39",
  },
  dark: {
    level0: "#2d2d2d",
    level1: "#0e4429",
    level2: "#006d32",
    level3: "#26a641",
    level4: "#39d353",
  },
};

const TIER_COLOR = {
  Advanced: "var(--hard)",
  Intermediate: "var(--medium)",
  Fundamental: "var(--easy)",
};

const Dashboard = () => {
  const { mode } = useTheme();
  const maxLang = Math.max(...languages.map((l) => l.count));
  const recent = numberedProjects.slice(0, 6);

  const segments = [
    { value: difficultyCounts.Easy, color: "var(--easy)" },
    { value: difficultyCounts.Medium, color: "var(--medium)" },
    { value: difficultyCounts.Hard, color: "var(--hard)" },
  ];

  return (
    <div className="dashboard">
      {/* -------- Profile sidebar card -------- */}
      <aside className="profile-card lc-card">
        <div className="profile-head">
          <LazyImage
            className="profile-avatar"
            src={profile.avatar}
            alt={profile.name}
            eager
          />
          <div className="profile-id">
            <h1 className="profile-name">{profile.name}</h1>
            <div className="profile-username">@{profile.username}</div>
            <div className="profile-rank">
              <FiAward /> Rank <b>{profile.rank}</b>
            </div>
          </div>
        </div>

        <div className="profile-actions">
          <a
            className="lc-btn lc-btn-primary"
            href={profile.resumePdf}
            download="Ravi_Kant_Sharma_CV.pdf"
          >
            <FiDownload /> Download CV
          </a>
          <Link className="lc-btn lc-btn-outline" to="/resume">
            <FiFileText /> View Resume
          </Link>
        </div>

        <ul className="profile-meta">
          <li>
            <FiBriefcase />
            <span>
              {profile.title} @ <b>{profile.company}</b>
            </span>
          </li>
          <li>
            <FiMapPin />
            <span>{profile.location}</span>
          </li>
          <li>
            <FiFileText />
            <span>{profile.education}</span>
          </li>
        </ul>

        <div className="profile-socials">
          {profile.socials.map((s) => {
            const Icon = SOCIAL_ICONS[s.id];
            return (
              <a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="icon-btn"
                aria-label={s.label}
                title={s.label}
              >
                {Icon && <Icon />}
              </a>
            );
          })}
        </div>

        <p className="profile-bio">{profile.bioShort}</p>
        <div className="profile-interests">
          {profile.interests.map((i) => (
            <span key={i} className="chip">
              {i}
            </span>
          ))}
        </div>
      </aside>

      {/* -------- Main column -------- */}
      <div className="dashboard-main">
        <div className="stat-strip">
          <div className="stat-strip-item">
            <span className="stat-strip-value">{difficultyCounts.total}</span>
            <span className="stat-strip-label">Solved</span>
          </div>
          <div className="stat-strip-item">
            <span className="stat-strip-value">{certificates.length}</span>
            <span className="stat-strip-label">Badges</span>
          </div>
          <div className="stat-strip-item">
            <span className="stat-strip-value">{totalTopics}</span>
            <span className="stat-strip-label">Topics</span>
          </div>
          <div className="stat-strip-item">
            <span className="stat-strip-value">{languages.length}</span>
            <span className="stat-strip-label">Languages</span>
          </div>
        </div>

        <div className="dash-row-2">
          {/* Solved ring */}
          <Card title="Projects Solved" className="solved-card">
            <div className="solved-body">
              <ProgressRing segments={segments} total={difficultyCounts.total}>
                <span className="ring-total">{difficultyCounts.total}</span>
                <span className="ring-sub">
                  <b>Solved</b>
                </span>
                <span className="ring-accept">100% acceptance</span>
              </ProgressRing>
              <div className="solved-breakdown">
                {["Easy", "Medium", "Hard"].map((d) => (
                  <div key={d} className={`breakdown-tile bt-${d.toLowerCase()}`}>
                    <span className="breakdown-label">{d}</span>
                    <span className="breakdown-count">
                      {difficultyCounts[d]}
                      <i>/{difficultyCounts[d]}</i>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Languages */}
          <Card title="Languages" className="lang-card">
            <ul className="lang-list">
              {languages.map((l) => {
                const Icon = getTechIcon(l.name);
                const color = getBrandColor(l.name, mode);
                return (
                  <li key={l.name} className="lang-item">
                    <div className="lang-item-head">
                      <span className="lang-name">
                        <Icon style={color ? { color } : undefined} /> {l.name}
                      </span>
                      <span className="lang-count">
                        {l.count} <i>projects</i>
                      </span>
                    </div>
                    <div className="lang-bar">
                      <span
                        style={{ width: `${(l.count / maxLang) * 100}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>

        {/* Skills tiers */}
        <Card title="Skills" className="skills-card">
          <div className="skill-tiers">
            {Object.entries(skillTiers).map(([tier, items]) => (
              <div key={tier} className="skill-tier">
                <div className="skill-tier-head">
                  <span
                    className="skill-dot"
                    style={{ background: TIER_COLOR[tier] }}
                  />
                  {tier}
                </div>
                <div className="skill-tier-pills">
                  {items.map((name) => (
                    <TopicPill key={name} name={name} size="sm" colorIcon />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Experience preview */}
        <Card
          title="Experience"
          className="exp-preview-card"
          action={
            <Link className="see-all" to="/experience">
              View all <FiChevronRight />
            </Link>
          }
        >
          <ul className="exp-preview-list">
            {experiences.map((exp) => (
              <li key={`${exp.company}-${exp.role}`} className="exp-preview-item">
                <span className="exp-preview-icon">
                  <FiBriefcase />
                </span>
                <div className="exp-preview-main">
                  <span className="exp-preview-role">{exp.role}</span>
                  <span className="exp-preview-company">
                    {exp.company}
                    {exp.current && (
                      <span className="exp-badge-current sm">Current</span>
                    )}
                  </span>
                </div>
                <span className="exp-preview-period">{exp.period}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Submission heatmap */}
        <Card
          title="Submission Activity"
          className="activity-card"
          action={
            <span className="muted-sm">public GitHub contributions</span>
          }
        >
          <div className="calendar-wrap">
            <GitHubCalendar
              username={profile.githubUsername}
              blockSize={12}
              blockMargin={4}
              fontSize={12}
              theme={CALENDAR_THEMES[mode]}
            />
          </div>
        </Card>

        <LeetCodeActivity />

        <div className="dash-row-2">
          {/* Badges preview */}
          <Card
            title="Badges"
            className="badges-preview-card"
            action={
              <Link className="see-all" to="/badges">
                View all <FiChevronRight />
              </Link>
            }
          >
            <div className="badges-preview">
              {certificates.slice(0, 4).map((b) => (
                <a
                  key={b.slug}
                  className="mini-badge"
                  href={b.url}
                  target="_blank"
                  rel="noreferrer"
                  title={b.title}
                >
                  <LazyImage src={b.image} alt={b.title} />
                  <span>{b.title}</span>
                </a>
              ))}
            </div>
          </Card>

          {/* Recent submissions */}
          <Card
            title="Recent Accepted Submissions"
            className="recent-card"
            action={
              <Link className="see-all" to="/problems">
                All problems <FiChevronRight />
              </Link>
            }
          >
            <ul className="recent-list">
              {recent.map((p) => (
                <li key={p.slug} className="recent-item">
                  <Link to={`/problems/${p.slug}`} className="recent-title">
                    {p.title}
                  </Link>
                  <span className="recent-right">
                    <DifficultyBadge difficulty={p.difficulty} />
                    <span className="recent-status">Accepted</span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
